import { useRef, useState, useCallback, useEffect } from 'react';

// ScrollFade — affordance condivisa per contenuti che scrollano in orizzontale
// oltre il loro contenitore (voci UX 16 e 17: tabella cantieri + barra 9-tab).
// Un solo pattern per due superfici: avvolge i figli in un contenitore
// `overflow-x:auto` e mostra un fade-gradient sul bordo sinistro/destro SOLO
// quando c'è altro contenuto in quella direzione — così l'utente vede che
// "c'è dell'altro" senza dover indovinare.
//
// Props:
//  - style          → applicato al wrapper esterno (es. borderBottom della tab-bar)
//  - fadeColor      → colore di partenza del gradiente (default sfondo pagina)
//  - fadeWidth      → larghezza della sfumatura in px (default 28)
//  - hideScrollbar  → nasconde la scrollbar nativa (usa .sf-scroller in index.css);
//                     la barra tab la nasconde, la tabella la lascia (affordance
//                     desktop utile con il mouse).
export default function ScrollFade({
  children,
  style,
  fadeColor = 'var(--ds-bg)',
  fadeWidth = 28,
  hideScrollbar = false,
}) {
  const scroller = useRef(null);
  const [fade, setFade] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setFade({
      left: el.scrollLeft > 1,
      right: el.scrollLeft < max - 1,
    });
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    // ResizeObserver: il fade va ricalcolato se cambia la larghezza del
    // contenitore O del contenuto (es. tabella che aggiunge/toglie righe).
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [update]);

  const edge = (side, show) => ({
    position: 'absolute', top: 0, bottom: 0, [side]: 0, width: fadeWidth,
    pointerEvents: 'none', zIndex: 2,
    background: `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, ${fadeColor}, transparent)`,
    opacity: show ? 1 : 0, transition: 'opacity 0.15s ease',
  });

  return (
    <div style={{ position: 'relative', ...style }}>
      <div
        ref={scroller}
        className={hideScrollbar ? 'sf-scroller' : undefined}
        style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
      >
        {children}
      </div>
      <div style={edge('left', fade.left)} />
      <div style={edge('right', fade.right)} />
    </div>
  );
}
